// =============================================================================
// Room Tracker alarm.
//
// Browsers refuse to play audio until the page has seen a real user gesture, so
// a reception terminal sitting idle overnight would ring silently at 3am with
// nobody any the wiser. Three things follow from that, and they are the whole
// design:
//
//   1. Unlock on the first gesture of the session — the login click will do —
//      and hold the AudioContext open for the life of the page.
//   2. Never assume it worked. `state` is published so the UI can show a banner
//      when sound is unavailable, because a silent alarm that looks armed is
//      worse than one that admits it is broken.
//   3. Never rely on sound alone. The screen carries the alarm too; this file
//      is the part that can fail.
//
// The cadence is deliberately intermittent — one 2s siren every 60s rather than
// a continuous tone. Continuous noise gets a terminal muted within the hour,
// and a muted terminal is silent for every future alarm as well.
//
// One cycle is global, not per room. Three overdue rooms ring once per minute
// between them; the count is carried visually. A room going red midway through
// a cycle joins the existing one rather than starting a second.
// =============================================================================

const ALARM_URL = '/sounds/room-alarm.wav';
const REPEAT_MS = 60_000;

export type AlarmState =
  | 'idle'        // never unlocked; no gesture seen yet
  | 'ready'       // unlocked and the siren is decoded
  | 'blocked'     // context exists but the browser suspended it
  | 'unsupported' // no Web Audio in this browser
  | 'error';      // the WAV could not be fetched or decoded

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext || w.webkitAudioContext || null;
}

class AlarmEngine {
  private ctx: AudioContext | null = null;
  private buffer: AudioBuffer | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private armed = false;
  private loadFailed = false;
  private gestureHooked = false;
  private listeners = new Set<() => void>();

  get state(): AlarmState {
    if (!audioContextCtor()) return 'unsupported';
    if (this.loadFailed) return 'error';
    if (!this.ctx) return 'idle';
    if (this.ctx.state !== 'running') return 'blocked';
    return this.buffer ? 'ready' : 'idle';
  }

  /** True while the repeating cycle is running. */
  get isArmed(): boolean {
    return this.armed;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  /**
   * Register one-shot listeners so the first click or keypress anywhere in the
   * app unlocks audio. Safe to call repeatedly; only the first call binds.
   */
  installGestureUnlock() {
    if (this.gestureHooked || typeof document === 'undefined') return;
    this.gestureHooked = true;

    const onGesture = () => { void this.unlock(); };
    document.addEventListener('pointerdown', onGesture, { once: true, capture: true });
    document.addEventListener('keydown', onGesture, { once: true, capture: true });

    // Browsers suspend an AudioContext while its tab is hidden. Without this,
    // a terminal left on the tracker overnight comes back muted.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) void this.resume();
    });
  }

  /** Create + resume the context and decode the siren. Needs a user gesture. */
  async unlock(): Promise<void> {
    const Ctor = audioContextCtor();
    if (!Ctor) { this.emit(); return; }

    if (!this.ctx) this.ctx = new Ctor();
    await this.resume();

    if (!this.buffer && !this.loadFailed) {
      try {
        const res = await fetch(ALARM_URL);
        if (!res.ok) throw new Error(`${ALARM_URL} → HTTP ${res.status}`);
        const bytes = await res.arrayBuffer();
        // The WAV is 31.25kHz; decodeAudioData resamples it to the context's
        // own rate, so the source file's rate does not need to match.
        this.buffer = await this.ctx.decodeAudioData(bytes);
      } catch {
        this.loadFailed = true;
      }
    }
    this.emit();
  }

  private async resume(): Promise<void> {
    if (!this.ctx) return;
    if (this.ctx.state !== 'running') {
      try { await this.ctx.resume(); } catch { /* stays 'blocked'; UI shows it */ }
    }
    this.emit();
  }

  /** Play once, now. Used by both the cycle and the Test button. */
  fire(): void {
    if (!this.ctx || !this.buffer || this.ctx.state !== 'running') {
      void this.resume();
      return;
    }
    const source = this.ctx.createBufferSource();
    source.buffer = this.buffer;
    source.connect(this.ctx.destination);
    source.start();
  }

  /**
   * Start the repeating cycle. Idempotent: calling it again while already armed
   * does nothing, which is what keeps three red rooms on one cadence instead of
   * three overlapping ones.
   */
  arm(): void {
    if (this.armed) return;
    this.armed = true;
    this.fire();                                  // first blast is immediate
    this.timer = setInterval(() => this.fire(), REPEAT_MS);
    this.emit();
  }

  /** Stop the cycle. Called when the last red room is ended. */
  disarm(): void {
    if (!this.armed) return;
    this.armed = false;
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    this.emit();
  }
}

export const alarm = new AlarmEngine();
